import { derived, writable } from 'svelte/store';

import { FontsService } from '@client/services/fonts';

/**
 * @typedef {Object} Axis
 * @property {string} tag
 * @property {number} min
 * @property {number} max
 * @property {number} step
 */

/**
 * @typedef {'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace' | string} Category
 */

/**
 * @typedef {Object} Font
 * @property {Axis[]=} axes
 * @property {Category} category
 * @property {string} family
 * @property {Object.<string, string>} files
 * @property {'webfonts#webfont'} kind
 * @property {string} lastModified
 * @property {string[]} subsets
 * @property {string[]} variants
 * @property {string} version
 */

/**
 * @typedef {Object} Response
 * @property {'webfonts#webfontList'} kind
 * @property {Font[]} items
 */

/** @type {import('svelte/store').Writable<Font[]>} */
const allFonts = writable([]);

/** @type {import('svelte/store').Writable<boolean>} */
export const loading = writable(true);

/** @type {string[]} */
export const popular = [
  'Arial',
  'Courier New',
  'Georgia',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
  'monospace',
  'sans-serif',
  'serif'
];

let loaded = false;

/** Fetches font data and updates the store */
async function fetchData() {
  loading.set(true);
  try {
    /** @type {Response} */
    const resp = await FontsService.getFonts();

    allFonts.set(resp.items);
  } catch (err) {
    console.error('Failed to fetch fonts:', err);
    allFonts.set([]);
  } finally {
    loading.set(false);
  }
}

/** @type {import('svelte/store').Readable<Font[]>} */
export const fonts = {
  subscribe(run) {
    if (!loaded) {
      loaded = true;
      fetchData();
    }
    return allFonts.subscribe(run);
  }
};

/** @type {import('svelte/store').Readable<string[]>} */
export const fontNames = derived(allFonts, ($allFonts) =>
  Array.isArray($allFonts) ? $allFonts.map((font) => font.family) : []
);
